// components/BentoSection.tsx
"use client";

import { motion, HTMLMotionProps } from "framer-motion";
import { Briefcase, GraduationCap, Heart, Quote } from "lucide-react";
import { useTranslations } from "next-intl";
import HexLive from "@/components/HexLive";


export default function BentoSection() {
  const b = useTranslations("bento");

  const hoverEffect = {
    scale: 1.02,
    y: -4,
    transition: { type: "spring", stiffness: 400, damping: 25 },
  } as const;

  return (
    <section className="relative py-24 bg-base overflow-hidden">
  {/* Decorative grid */}
  <div
    className="pointer-events-none absolute inset-0"
    style={{
      backgroundImage: `
        linear-gradient(rgb(var(--accent) / 0.10) 1px, transparent 1px),
        linear-gradient(90deg, rgb(var(--accent) / 0.10) 1px, transparent 1px)
      `,
      backgroundSize: "60px 60px",
      maskImage: "radial-gradient(ellipse 70% 65% at 50% 50%, black 30%, transparent 100%)",
      WebkitMaskImage: "radial-gradient(ellipse 70% 65% at 50% 50%, black 30%, transparent 100%)",
    }}
  />

  {/* Radial glow */}
  <div
    className="pointer-events-none absolute inset-0"
    style={{
      background:
        "radial-gradient(ellipse 60% 40% at 30% 50%, rgb(var(--accent) / 0.08) 0%, transparent 70%)",
    }}
  />

  {/* Decorative hexagons — 12 шт, разного размера */}
  <HexLive className="hidden md:block absolute left-[4%] top-16 w-20 h-20"       mode="pulse" baseOpacity={0.14} intervalSec={44} />
  <HexLive className="hidden md:block absolute left-[10%] top-1/3 w-12 h-12"     mode="spark" baseOpacity={0.20} intervalSec={30} />
  <HexLive className="hidden md:block absolute left-[3%] bottom-24 w-28 h-28"    mode="pulse" baseOpacity={0.10} intervalSec={58} />
  <HexLive className="hidden md:block absolute left-[18%] bottom-8 w-16 h-16"    mode="spark" baseOpacity={0.14} intervalSec={36} />
  <HexLive className="hidden md:block absolute left-1/3 top-10 w-14 h-14"        mode="spark" baseOpacity={0.16} intervalSec={40} />
  <HexLive className="hidden md:block absolute left-[45%] bottom-16 w-10 h-10"   mode="pulse" baseOpacity={0.18} intervalSec={33} />
  <HexLive className="hidden md:block absolute right-[6%] top-20 w-24 h-24"      mode="pulse" baseOpacity={0.12} intervalSec={50} />
  <HexLive className="hidden md:block absolute right-[14%] top-1/2 w-16 h-16"    mode="spark" baseOpacity={0.18} intervalSec={34} />
  <HexLive className="hidden md:block absolute right-[4%] bottom-28 w-32 h-32"   mode="pulse" baseOpacity={0.09} intervalSec={60} />
  <HexLive className="hidden md:block absolute right-[20%] bottom-6 w-12 h-12"   mode="spark" baseOpacity={0.20} intervalSec={28} />
  <HexLive className="hidden md:block absolute right-[30%] top-8 w-14 h-14"      mode="pulse" baseOpacity={0.15} intervalSec={46} />
  <HexLive className="hidden md:block absolute right-[38%] bottom-20 w-20 h-20"  mode="spark" baseOpacity={0.11} intervalSec={42} />

  <div className="relative z-10 max-w-7xl mx-auto px-6">

        {/* Заголовок */}
        <div className="mb-16 text-start">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-fore mb-8 leading-tight">
            {b("mainTitle")}
          </h2>
          <p className="text-xl text-sec max-w-2xl">{b("mainSubtitle")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">

          {/* Большая карточка 1 — Студенты: экзамены */}
          <motion.div
            whileHover={hoverEffect}
            className="relative overflow-hidden md:col-span-8 border border-base rounded-[32px] p-10 bg-card shadow-card hover:shadow-accent flex flex-col justify-between transition-shadow"
          >
            <MathDecor />

            <div className="relative z-10 inline-flex items-center gap-2 mb-6">
              <GraduationCap className="w-5 h-5 text-accent" />
              <span className="text-xs font-semibold uppercase tracking-wider text-accent">
                {b("students.badge")}
              </span>
            </div>
            <div className="relative z-10">
              <h3 className="text-3xl font-bold text-fore mb-3">{b("students.exams.title")}</h3>
              <p className="text-sec text-lg">{b("students.exams.desc")}</p>
            </div>
          </motion.div>

          {/* Маленькая карточка 2 — Студенты: контент */}
          <motion.div
            whileHover={hoverEffect}
            className="relative overflow-hidden md:col-span-4 border border-base rounded-[32px] p-10 bg-card shadow-card hover:shadow-accent flex flex-col justify-between transition-shadow"
          >
            <Briefcase className="w-12 h-12 text-accent mb-8" />
            <div>
              <h3 className="text-2xl font-bold text-fore mb-3">{b("students.career.title")}</h3>
              <p className="text-sec">{b("students.career.desc")}</p>
            </div>
          </motion.div>

          {/* Маленькая карточка 3 — Родители: спокойствие */}
          <motion.div
            whileHover={hoverEffect}
            className="relative overflow-hidden md:col-span-4 border border-base rounded-[32px] p-10 bg-card shadow-card hover:shadow-accent flex flex-col justify-between transition-shadow"
          >
            <Heart className="w-12 h-12 text-accent mb-8" />
            <div>
              <h3 className="text-2xl font-bold text-fore mb-3">{b("parents.peace.title")}</h3>
              <p className="text-sec">{b("parents.peace.desc")}</p>
            </div>
          </motion.div>

          {/* Большая карточка 4 — Родители: бюджет */}
          <motion.div
            whileHover={hoverEffect}
            className="relative overflow-hidden md:col-span-8 border border-base rounded-[32px] p-10 bg-card shadow-card hover:shadow-accent flex flex-col justify-between transition-shadow"
          >
            <BudgetDecor tags={{
              kids: b("budgetTags.kids"),
              future: b("budgetTags.future"),
              home: b("budgetTags.home"),
            }} />

            <div className="relative z-10 inline-flex items-center gap-2 mb-6">
              <Heart className="w-5 h-5 text-accent" />
              <span className="text-xs font-semibold uppercase tracking-wider text-accent">
                {b("parents.badge")}
              </span>
            </div>
            <div className="relative z-10">
              <h3 className="text-3xl font-bold text-fore mb-3">{b("parents.budget.title")}</h3>
              <p className="text-sec text-lg">{b("parents.budget.desc")}</p>
            </div>
          </motion.div>

        </div>

        {/* Community quote */}
        <motion.div
          whileHover={hoverEffect}
          style={{ borderColor: "#00359c", backgroundColor: "rgba(0, 53, 156, 0.08)" }}
          className="mt-8 border rounded-[32px] p-10 shadow-card hover:shadow-accent transition-shadow"
        >
          <Quote className="w-10 h-10 text-accent mb-4 opacity-60" />
          <p className="text-2xl md:text-3xl font-semibold text-fore mb-3 leading-tight">
            {b("community.title")}
          </p>
          <p className="text-accent text-base font-semibold mb-2">{b("community.subtitle")}</p>
          <p className="text-sec text-lg">{b("community.desc")}</p>
        </motion.div>

        {/* CTA */}
        {/* CTA */}
        <div className="mt-16 flex flex-col items-center text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-fore mb-10 leading-tight text-center">
            {b("ctaFooter")}
          </h2>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            className="px-12 py-5 bg-accent text-white font-semibold text-lg rounded-2xl shadow-accent hover:opacity-90 transition-opacity"
          >
            {b("cta")}
          </motion.button>
          <p className="mt-4 text-sm text-mute">{b("trust")}</p>
        </div>

      </div>
    </section>
  );
}

/* ============================================================ */
/*  Декоративные элементы                                       */
/* ============================================================ */

function MathDecor() {
  return (
    <div className="pointer-events-none absolute inset-0 select-none overflow-hidden text-accent" aria-hidden>
      <div className="absolute top-4 end-6 text-[140px] font-serif leading-none opacity-[0.05] rotate-12">∫</div>
      <div className="absolute -bottom-4 end-32 text-[120px] font-serif leading-none opacity-[0.05] -rotate-6">π</div>
      <div className="absolute top-10 end-44 text-3xl font-serif opacity-[0.08] rotate-3">a² + b² = c²</div>
      <div className="absolute top-32 end-12 text-2xl font-serif opacity-[0.10]">E = mc²</div>
      <div className="absolute top-56 end-56 text-xl font-serif opacity-[0.10] -rotate-3">f(x) = ax + b</div>
      <div className="absolute bottom-32 end-8 text-2xl font-serif opacity-[0.08] rotate-6">∑ n=1</div>
      <div className="absolute bottom-12 end-60 text-xl font-serif opacity-[0.10]">√(x² + y²)</div>
      <div className="absolute top-20 end-72 text-base opacity-[0.12]">∂y/∂x</div>
      <div className="absolute bottom-44 end-44 text-lg opacity-[0.10] rotate-12">θ</div>
      <div className="absolute top-44 end-28 text-base opacity-[0.10]">log₂</div>

      <svg className="absolute bottom-6 end-72 w-24 h-24 opacity-[0.08]" viewBox="0 0 100 100" fill="none">
        <circle cx="50" cy="50" r="35" stroke="currentColor" strokeWidth="2" strokeDasharray="3 3" />
        <path d="M50 15 L85 65 L15 65 Z" stroke="currentColor" strokeWidth="2" fill="none" />
      </svg>
      <svg className="absolute top-72 end-20 w-20 h-20 opacity-[0.08] rotate-12" viewBox="0 0 100 100" fill="none">
        <path d="M20 80 L20 20 L80 20" stroke="currentColor" strokeWidth="2" />
        <path d="M20 80 L80 80" stroke="currentColor" strokeWidth="2" />
        <path d="M20 80 Q40 50 80 30" stroke="currentColor" strokeWidth="2" fill="none" />
      </svg>
    </div>
  );
}

function BudgetDecor({ tags }: { tags: { kids: string; future: string; home: string } }) {
  return (
    <div className="pointer-events-none absolute inset-0 select-none overflow-hidden" aria-hidden>
      <div
        className="absolute -end-20 -top-20 h-80 w-80 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, rgb(var(--accent) / 0.08), transparent 70%)" }}
      />
      <div
        className="absolute bottom-[-10%] end-[10%] h-60 w-60 rounded-full blur-2xl"
        style={{ background: "radial-gradient(circle, rgb(var(--accent) / 0.04), transparent 70%)" }}
      />

      <svg
        className="absolute bottom-2 end-4 h-48 w-48 text-accent opacity-[0.08]"
        viewBox="0 0 200 200"
        fill="none"
        stroke="currentColor"
      >
        <path d="M20 150 A 80 80 0 0 1 180 150" strokeWidth="1.5" strokeDasharray="4 4" />
        <path d="M60 160 V105 L100 70 L140 105 V160 H60 Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M90 78 H110" strokeWidth="3" strokeLinecap="round" />
        <path
          d="M100 135 C100 135 88 125 88 117 C88 111 93 107 98 107 C101 107 103 109 100 113 C97 109 99 107 102 107 C107 107 112 111 112 117 C112 125 100 135 100 135 Z"
          fill="currentColor"
          fillOpacity="0.4"
          strokeWidth="1"
        />
        <path d="M170 60 C140 40 110 45 100 70" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="3 6" />
        <circle cx="170" cy="60" r="3" fill="currentColor" />
        <circle cx="135" cy="44" r="4" fill="currentColor" fillOpacity="0.6" />
      </svg>

      <div className="absolute top-12 end-12 flex items-center gap-1.5 rounded-full border border-accent-sm bg-accent-soft px-2.5 py-1 text-[11px] font-medium tracking-wide text-sec rotate-6">
        <span className="h-1.5 w-1.5 rounded-full bg-accent opacity-50" />
        <span>{tags.kids}</span>
      </div>
      <div className="absolute top-28 end-48 flex items-center gap-1.5 rounded-full border border-accent-sm bg-accent-soft px-2.5 py-1 text-[11px] font-medium tracking-wide text-sec -rotate-3">
        <span className="h-1.5 w-1.5 rounded-full bg-accent opacity-50" />
        <span>{tags.future}</span>
      </div>
      <div className="absolute bottom-28 end-10 flex items-center gap-1.5 rounded-full border border-accent-sm bg-accent-soft px-2.5 py-1 text-[11px] font-medium tracking-wide text-sec -rotate-6">
        <span className="h-1.5 w-1.5 rounded-full bg-accent opacity-50" />
        <span>{tags.home}</span>
      </div>
    </div>
  );
}

/* ============================================================ */
/*  Neural PCB — branching network on grid                      */
/* ============================================================ */

